import { Helmet } from "react-helmet-async";
import { useLocation } from "@/lib/router-compat";
import { routeLanguage, routeOgLocale } from '@/lib/seo/route-locale';

interface SEOHeadProps {
  title: string;
  description: string;
  canonicalPath?: string;
  ogImage?: string;
  noindex?: boolean;
  structuredData?: object;
  lang?: 'ro' | 'en' | 'de';
}

const BASE_URL = 'https://roautomarket.ro';

/**
 * Centralized SEO component for consistent meta tags across all pages
 * Ensures every page has proper title, description, html lang, and social meta tags
 * ALWAYS sets canonical URL - either from prop or from current path
 */
export function SEOHead({
  title,
  description,
  canonicalPath,
  ogImage = "/og-roautomarket.png",
  noindex = false,
  structuredData,
  lang
}: SEOHeadProps) {
  const location = useLocation();
  
  // Get clean path without query params or hash
  const currentPath = location.pathname.replace(/\/$/, '') || '/';
  
  // Use provided canonicalPath or fall back to current location
  const effectiveCanonicalPath = canonicalPath !== undefined ? canonicalPath : currentPath;
  
  // Auto-detect language from path
  const detectLang = (): 'ro' | 'en' | 'de' => {
    if (lang) return lang;
    return routeLanguage(currentPath);
  };
  
  const language = detectLang();
  const ogLocale = routeOgLocale(currentPath);
  
  const fullTitle = title.includes("RO Auto Market") ? title : `${title} | RO Auto Market`;
  const canonicalUrl = `${BASE_URL}${effectiveCanonicalPath}`;
  const fullOgImage = ogImage.startsWith("http") ? ogImage : `${BASE_URL}${ogImage}`;

  return (
    <Helmet>
      {/* HTML Lang Attribute */}
      <html lang={language} />
      
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      
      {/* Canonical URL - ALWAYS set to prevent index.html default */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {(noindex || /^\/(de|uk)(\/|$)/.test(currentPath)) && <meta name="robots" content="noindex,nofollow" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="RO Auto Market" />
      <meta property="og:locale" content={ogLocale} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@roautomarket" />
      <meta name="twitter:creator" content="@roautomarket" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:image:alt" content={title} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}
